import { t } from "../../i18n";
import { useCallback, useEffect, useState } from "react";
import { LoaderCircle, Save, ShieldCheck, Trash2 } from "lucide-react";
import type { ConversationCommandRule } from "@lumen/shared";
import { api } from "../../api";

export function ConversationCommandRulesPanel({
  conversationId,
  onClose: _onClose,
}: {
  conversationId: string;
  onClose?: () => void;
}) {
  const [rules, setRules] = useState<ConversationCommandRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    void api<ConversationCommandRule[]>(
      `/conversations/${conversationId}/command-rules`,
    )
      .then(setRules)
      .catch((reason) =>
        setError(
          reason instanceof Error
            ? reason.message
            : t("Command rules could not be loaded"),
        ),
      )
      .finally(() => setLoading(false));
  }, [conversationId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateRule = (
    id: string,
    change: Partial<Pick<ConversationCommandRule, "kind" | "pattern">>,
  ) =>
    setRules((current) =>
      current.map((rule) => (rule.id === id ? { ...rule, ...change } : rule)),
    );

  const save = async (rule: ConversationCommandRule) => {
    if (!rule.pattern.trim()) return;
    setBusyId(rule.id);
    setError("");
    try {
      await api(`/conversations/${conversationId}/command-rules/${rule.id}`, {
        method: "PATCH",
        body: JSON.stringify({ kind: rule.kind, pattern: rule.pattern }),
      });
      load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Could not save command rule"),
      );
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (rule: ConversationCommandRule) => {
    setBusyId(rule.id);
    setError("");
    try {
      await api(`/conversations/${conversationId}/command-rules/${rule.id}`, {
        method: "DELETE",
      });
      setRules((current) => current.filter((item) => item.id !== rule.id));
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t("Could not remove command rule"),
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-stone-200 px-4 dark:border-white/10">
        <div className="grid size-7 place-items-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
          <ShieldCheck size={15} />
        </div>
        <div className="min-w-0">
          <h2 className="truncate text-sm font-bold">
            {t("Command approvals")}
          </h2>
          <p className="text-[11px] text-stone-500">
            {t("Accepted in this chat")}
          </p>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
          {t(
            "These rules allow matching commands in this chat until you remove them. Workspace denies still apply.",
          )}
        </p>
        {loading ? (
          <div className="grid min-h-48 place-items-center text-stone-400">
            <LoaderCircle className="animate-spin" />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        ) : rules.length ? (
          <div className="space-y-3">
            {rules.map((rule) => {
              const busy = busyId === rule.id;
              return (
                <div
                  key={rule.id}
                  className="rounded-xl border border-stone-200 p-3 dark:border-white/10"
                >
                  <div className="mb-2 flex items-center justify-between gap-2 text-[11px] text-stone-500">
                    <span className="truncate">{rule.workspaceName}</span>
                    <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                      {rule.kind === "exact" ? t("Exact") : t("Pattern")}
                    </span>
                  </div>
                  <select
                    className="field mb-2 py-2 text-xs"
                    value={rule.kind}
                    disabled={busy}
                    onChange={(event) =>
                      updateRule(rule.id, {
                        kind: event.target.value as "exact" | "pattern",
                      })
                    }
                  >
                    <option value="exact">{t("Exact command")}</option>
                    <option value="pattern">
                      {t("Similar command pattern")}
                    </option>
                  </select>
                  <textarea
                    className="field resize-y font-mono text-xs"
                    rows={3}
                    value={rule.pattern}
                    disabled={busy}
                    onChange={(event) =>
                      updateRule(rule.id, { pattern: event.target.value })
                    }
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      type="button"
                      className="button p-2 text-stone-400 hover:text-red-600"
                      disabled={busy}
                      onClick={() => void remove(rule)}
                      title={t("Remove rule")}
                    >
                      {busy ? (
                        <LoaderCircle className="animate-spin" size={14} />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                    <button
                      type="button"
                      className="button-primary px-2.5 py-1.5 text-xs"
                      disabled={busy || !rule.pattern.trim()}
                      onClick={() => void save(rule)}
                    >
                      {busy ? (
                        <LoaderCircle className="animate-spin" size={13} />
                      ) : (
                        <Save size={13} />
                      )}
                      {t("Save")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-stone-200 p-5 text-center text-sm text-stone-500 dark:border-white/10">
            {t("No conversation command rules yet.")}
          </div>
        )}
      </div>
    </div>
  );
}
